import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase from '../../../config/supabaseClient';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};

// Chave para armazenar informações de sessão no localStorage
const SESSION_STORAGE_KEY = 'supabase.auth.token';
const USER_SESSION_KEY = 'userSession';
const LAST_AUTH_EVENT_KEY = 'lastAuthEvent';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSessionRestored, setIsSessionRestored] = useState(false);

    // Usar ref para controlar eventos de autenticação
    const authEventRef = useRef({
        lastEventTime: 0,
        lastEventType: null,
        isManualAuth: false,
        isInitialLoad: true
    });

    // Função para verificar se há uma sessão válida no localStorage
    const checkLocalStorageSession = () => {
        try {
            const storedSession = localStorage.getItem(SESSION_STORAGE_KEY);
            return !!storedSession;
        } catch (error) {
            console.error('Erro ao verificar sessão no localStorage:', error);
            return false;
        }
    };

    // Função para salvar o último evento de autenticação
    const saveLastAuthEvent = (eventType) => {
        try {
            const now = Date.now();
            const eventData = {
                type: eventType,
                timestamp: now
            };
            localStorage.setItem(LAST_AUTH_EVENT_KEY, JSON.stringify(eventData));

            // Atualizar a ref
            authEventRef.current = {
                ...authEventRef.current,
                lastEventTime: now,
                lastEventType: eventType
            };
        } catch (error) {
            console.error('Erro ao salvar evento de autenticação:', error);
        }
    };

    // Função para verificar se um evento de autenticação deve ser processado
    const shouldProcessAuthEvent = (eventType) => {
        try {
            const now = Date.now();

            // Se for um evento manual de login/logout, sempre processar
            if (authEventRef.current.isManualAuth &&
                (eventType === 'SIGNED_IN' || eventType === 'SIGNED_OUT')) {
                return true;
            }

            // Se for a carga inicial, não mostrar mensagens
            if (authEventRef.current.isInitialLoad) {
                return false;
            }

            // Se for um evento de renovação de token, não mostrar mensagens
            if (eventType === 'TOKEN_REFRESHED') {
                return false;
            }

            // Verificar se o mesmo evento ocorreu recentemente (menos de 2 segundos)
            const timeSinceLastEvent = now - authEventRef.current.lastEventTime;
            if (timeSinceLastEvent < 2000 && eventType === authEventRef.current.lastEventType) {
                return false;
            }

            // Verificar se o evento veio de outra aba
            const lastEventData = localStorage.getItem(LAST_AUTH_EVENT_KEY);
            if (lastEventData) {
                const { type, timestamp } = JSON.parse(lastEventData);
                const timeSinceStoredEvent = now - timestamp;

                // Se o mesmo evento ocorreu em outra aba nos últimos 2 segundos
                if (timeSinceStoredEvent < 2000 && type === eventType) {
                    return false;
                }
            }

            return true;
        } catch (error) {
            console.error('Erro ao verificar evento de autenticação:', error);
            return false;
        }
    };

    // Verificar sessão atual e configurar listener para mudanças de autenticação
    useEffect(() => {
        // Função para verificar a sessão atual
        const checkSession = async () => {
            try {
                setLoading(true);

                // Verificar se há uma sessão no localStorage antes de fazer a chamada de API
                const hasLocalSession = checkLocalStorageSession();

                if (!hasLocalSession) {
                    setUser(null);
                    setSession(null);
                    setLoading(false);
                    return;
                }

                // Buscar sessão atual do Supabase
                const { data, error } = await supabase.auth.getSession();

                if (error) {
                    throw error;
                }

                if (data && data.session) {
                    setSession(data.session);
                    setUser(data.session.user);
                    setIsSessionRestored(true);

                    // Salvar a sessão no localStorage para persistência entre abas
                    try {
                        localStorage.setItem(USER_SESSION_KEY, JSON.stringify({
                            id: data.session.user.id,
                            email: data.session.user.email,
                            lastActive: new Date().toISOString()
                        }));
                    } catch (storageError) {
                        console.error('Erro ao salvar sessão no localStorage:', storageError);
                    }
                } else {
                    setUser(null);
                    setSession(null);

                    // Limpar dados de sessão do localStorage
                    try {
                        localStorage.removeItem(USER_SESSION_KEY);
                    } catch (storageError) {
                        console.error('Erro ao remover sessão do localStorage:', storageError);
                    }
                }
            } catch (error) {
                console.error('Erro ao verificar sessão:', error);
                setUser(null);
                setSession(null);
            } finally {
                setLoading(false);

                // Após a primeira verificação, não é mais a carga inicial
                authEventRef.current.isInitialLoad = false;
            }
        };

        // Verificar sessão inicial
        checkSession();

        // Configurar listener para mudanças de autenticação
        const { data: authListener } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
                // Registrar evento apenas para depuração, sem console.log em produção
                if (process.env.NODE_ENV !== 'production') {
                    console.log('Auth state changed:', event);
                }

                // Verificar se devemos processar este evento
                const shouldProcess = shouldProcessAuthEvent(event);

                // Salvar o evento atual
                saveLastAuthEvent(event);

                if (newSession) {
                    setSession(newSession);
                    setUser(newSession.user);

                    // Salvar a sessão no localStorage para persistência entre abas
                    try {
                        localStorage.setItem(USER_SESSION_KEY, JSON.stringify({
                            id: newSession.user.id,
                            email: newSession.user.email,
                            lastActive: new Date().toISOString()
                        }));
                    } catch (storageError) {
                        console.error('Erro ao salvar sessão no localStorage:', storageError);
                    }

                    // Mostrar mensagem apenas se devemos processar este evento
                    if (shouldProcess && event === 'SIGNED_IN' && authEventRef.current.isManualAuth) {
                        toast.success('Login realizado com sucesso!');
                        // Resetar flag após mostrar a mensagem
                        authEventRef.current.isManualAuth = false;
                    }
                } else {
                    setSession(null);
                    setUser(null);

                    // Limpar dados de sessão do localStorage
                    try {
                        localStorage.removeItem(USER_SESSION_KEY);
                    } catch (storageError) {
                        console.error('Erro ao remover sessão do localStorage:', storageError);
                    }

                    // Mostrar mensagem apenas se devemos processar este evento
                    if (shouldProcess && event === 'SIGNED_OUT' && authEventRef.current.isManualAuth) {
                        toast.success('Logout realizado com sucesso!');
                        // Resetar flag após mostrar a mensagem
                        authEventRef.current.isManualAuth = false;
                    }
                }

                setLoading(false);
            }
        );

        // Adicionar listener para eventos de armazenamento para sincronizar entre abas
        const handleStorageChange = (event) => {
            if (event.key === SESSION_STORAGE_KEY || event.key === USER_SESSION_KEY) {
                // Não mostrar toast ao sincronizar entre abas
                authEventRef.current.isInitialLoad = true;
                checkSession();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            authListener.subscription.unsubscribe();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Função para login
    const login = async (email, password) => {
        try {
            setLoading(true);
            // Definir flag para indicar que é uma autenticação manual
            authEventRef.current.isManualAuth = true;
            authEventRef.current.isInitialLoad = false;

            // Opções para permitir múltiplas sessões simultâneas
            const options = {
                email,
                password,
                options: {
                    multiTab: true // Permitir múltiplas sessões simultâneas
                }
            };

            const { data, error } = await supabase.auth.signInWithPassword(options);

            if (error) throw error;

            // Verificar se o usuário está ativo na tabela funcionariousuario
            if (data.user) {
                const { data: userData, error: userError } = await supabase
                    .from('funcionariousuario')
                    .select('ativo, nome, setor')
                    .eq('uid', data.user.id)
                    .single();

                if (userError) {
                    // Se não encontrar o usuário na tabela, permitir login (pode ser admin inicial)
                    console.log('Usuário não encontrado na tabela usuarios, permitindo login');
                } else if (userData && !userData.ativo) {
                    // Usuário encontrado mas está desabilitado
                    await supabase.auth.signOut(); // Fazer logout imediatamente
                    throw new Error('Sua conta foi desabilitada. Entre em contato com o administrador.');
                } else if (userData && userData.setor !== null) {
                    // Usuário é colaborador (tem setor) - não pode acessar o painel web
                    await supabase.auth.signOut(); // Fazer logout imediatamente
                    throw new Error('Colaboradores não têm acesso ao painel web. Use o aplicativo móvel.');
                }
            }

            return { success: true, data };
        } catch (error) {
            console.error('Erro ao fazer login:', error);
            toast.error(error.message || 'Erro ao fazer login');
            return { success: false, error };
        } finally {
            setLoading(false);
        }
    };

    // Função para logout
    const logout = async () => {
        try {
            setLoading(true);
            // Definir flag para indicar que é uma autenticação manual
            authEventRef.current.isManualAuth = true;
            authEventRef.current.isInitialLoad = false;

            // Tentar fazer logout, mas tratar erros de sessão graciosamente
            try {
                const { error } = await supabase.auth.signOut();
                if (error) {
                    // Se for erro de sessão ausente, não é um erro crítico
                    if (error.message?.includes('Auth session missing') || 
                        error.name === 'AuthSessionMissingError') {
                        console.log('Sessão já estava ausente, limpando estado local');
                    } else {
                        throw error;
                    }
                }
            } catch (signOutError) {
                // Se for erro de sessão ausente ou 403, tratar como logout bem-sucedido
                if (signOutError.message?.includes('Auth session missing') || 
                    signOutError.name === 'AuthSessionMissingError' ||
                    signOutError.message?.includes('403') ||
                    signOutError.message?.includes('Forbidden')) {
                    console.log('Erro de sessão tratado graciosamente:', signOutError.message);
                } else {
                    // Para outros erros, re-lançar
                    throw signOutError;
                }
            }

            // Sempre limpar o estado local independentemente do resultado do signOut
            setSession(null);
            setUser(null);

            // Limpar dados de sessão do localStorage
            try {
                localStorage.removeItem(USER_SESSION_KEY);
            } catch (storageError) {
                console.error('Erro ao remover sessão do localStorage:', storageError);
            }

            // Mostrar mensagem de sucesso apenas se for logout manual
            if (authEventRef.current.isManualAuth) {
                toast.success('Logout realizado com sucesso!');
                authEventRef.current.isManualAuth = false;
            }

        } catch (error) {
            console.error('Erro ao fazer logout:', error);
            
            // Mesmo com erro, limpar o estado local
            setSession(null);
            setUser(null);
            try {
                localStorage.removeItem(USER_SESSION_KEY);
            } catch (storageError) {
                console.error('Erro ao remover sessão do localStorage:', storageError);
            }
            
            toast.error('Erro ao fazer logout, mas sessão foi limpa localmente');
        } finally {
            setLoading(false);
        }
    };

    // Verificar se o token está expirado e precisa ser renovado
    const checkAndRefreshToken = async (silent = true, forceLogoutOtherTabs = true) => {
        if (!session) return false;

        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

        // Se o token expira em menos de 5 minutos, renovar
        if (expiresAt < fiveMinutesFromNow) {
            try {
                // Definir flag para não mostrar toast durante renovação de token
                if (silent) {
                    authEventRef.current.isInitialLoad = true;
                }

                // Opção para permitir múltiplas sessões simultâneas
                const options = {};
                if (!forceLogoutOtherTabs) {
                    options.multiTab = true;
                }

                const { data, error } = await supabase.auth.refreshSession(options);
                if (error) throw error;

                setSession(data.session);
                setUser(data.session?.user || null);

                // Atualizar a sessão no localStorage
                if (data.session) {
                    try {
                        localStorage.setItem(USER_SESSION_KEY, JSON.stringify({
                            id: data.session.user.id,
                            email: data.session.user.email,
                            lastActive: new Date().toISOString()
                        }));
                    } catch (storageError) {
                        console.error('Erro ao salvar sessão no localStorage:', storageError);
                    }
                }

                return true;
            } catch (error) {
                console.error('Erro ao renovar token:', error);
                return false;
            }
        }

        return true;
    };

    // Verificar e renovar token a cada 4 minutos
    useEffect(() => {
        const interval = setInterval(() => {
            if (session) {
                checkAndRefreshToken(true, false); // Adicionado false para não forçar logout em outras abas
            }
        }, 4 * 60 * 1000); // 4 minutos

        return () => clearInterval(interval);
    }, [session]);

    // Verificar se o usuário ainda está ativo a cada 2 minutos
    useEffect(() => {
        const checkUserStatus = async () => {
            if (!user) return;

            try {
                const { data, error } = await supabase
                    .from('funcionariousuario')
                    .select('ativo')
                    .eq('uid', user.id)
                    .single();

                if (!error && data && !data.ativo) {
                    // Usuário foi desabilitado, fazer logout
                    toast.error('Sua conta foi desabilitada. Você será desconectado.');
                    await logout();
                }
            } catch (error) {
                // Ignorar erros silenciosamente para não afetar a experiência do usuário
                console.log('Erro ao verificar status do usuário:', error);
            }
        };

        const interval = setInterval(checkUserStatus, 2 * 60 * 1000); // 2 minutos

        return () => clearInterval(interval);
    }, [user]);

    const value = {
        user,
        session,
        loading,
        isSessionRestored,
        login,
        logout,
        checkAndRefreshToken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};